import { Link } from 'react-router-dom'

import type {
  PokerActiveStandingFragment,
  PokerEliminatedFragment,
} from '../../../__generated__/hooks'
import { IconPokerChip } from '../../../components/Icon'
import StandingsRow from '../../../components/StandingsRow'

/**
 * Compact standings card for Bordet — active above, "Slått ut" divider,
 * eliminated below. Caps each section at 6 rows and links to the
 * full-page /stack route for the rest.
 *
 * Logic mirrors the Stack page's full version; this one is constrained
 * for the "single glance" use case. If you want the whole list, tap
 * "Se hele leaderboard" → /stack.
 */
const StandingsBoard = ({
  active,
  eliminated,
  yourSeatId,
  isFinished,
  playerKey,
}: {
  active: ReadonlyArray<PokerActiveStandingFragment>
  eliminated: ReadonlyArray<PokerEliminatedFragment>
  yourSeatId: string | undefined
  isFinished: boolean
  playerKey: string
}) => {
  if (!active.length && !eliminated.length) return null

  const sortedActive = [...active].sort(
    (a, b) => b.currentStack - a.currentStack
  )
  const sortedElim = [...eliminated].sort(
    (a, b) => a.finishingPosition - b.finishingPosition
  )

  const compactLimit = 6
  const shownActive = sortedActive.slice(0, compactLimit)
  const activeOverflow = sortedActive.length - shownActive.length
  const shownElim = sortedElim.slice(0, compactLimit)
  const elimOverflow = sortedElim.length - shownElim.length
  const totalPlayers = sortedActive.length + sortedElim.length

  return (
    <div className="mx-2 my-3 bg-paper rounded-lg shadow overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 border-b border-ink-100">
        <span className="up text-ink-500 flex items-center gap-2">
          <IconPokerChip size={14} className="text-gold" />
          {isFinished ? 'Sluttresultat' : 'Leaderboard'}
        </span>
        <span className="text-xs text-ink-500 num">
          {sortedActive.length}/{totalPlayers} igjen
        </span>
      </div>

      {shownActive.length > 0 ? (
        <ol>
          {shownActive.map((s, i) => (
            <StandingsRow
              key={s.id}
              playerName={s.playerName}
              isYou={s.id === yourSeatId}
              variant={{
                kind: 'active',
                rank: i + 1,
                currentStack: s.currentStack,
              }}
            />
          ))}
        </ol>
      ) : null}

      {/* "Slått ut" divider — only when both sides have rows. */}
      {shownElim.length > 0 && shownActive.length > 0 ? (
        <div className="flex items-center gap-2 px-4 py-1.5 bg-cream-card border-t border-b border-ink-100">
          <span className="flex-1 h-px bg-ink-200" aria-hidden />
          <span className="up text-[10px] text-ink-500">Slått ut</span>
          <span className="flex-1 h-px bg-ink-200" aria-hidden />
        </div>
      ) : null}

      {shownElim.length > 0 ? (
        <ol>
          {shownElim.map((e) => (
            <StandingsRow
              key={e.id}
              playerName={e.playerName}
              isYou={e.id === yourSeatId}
              variant={{
                kind: 'eliminated',
                finishingPosition: e.finishingPosition,
              }}
            />
          ))}
        </ol>
      ) : null}

      {/* "Se hele stillingen" — primary affordance to /stack when there's
          more than the compact list shows. Replaces the old "+ N til"
          dead-end text. */}
      {activeOverflow > 0 || elimOverflow > 0 ? (
        <Link
          to={`/s/${playerKey}/stack`}
          className="block text-center text-sm font-semibold text-green-700 py-2.5 border-t border-ink-100 active:opacity-70"
        >
          Se hele leaderboardet ({totalPlayers}) →
        </Link>
      ) : null}
    </div>
  )
}

export default StandingsBoard
